<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        View Chapters
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="'View chapters for each course.'">
          info
        </i>
      </h2>
      <div>
        <b-btn size="md" variant="primary" :to="{ name: 'create-chapter' }">
          Add Chapter
        </b-btn>
      </div>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Chapters"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>
              <div v-if="!isTabSmallScreen" class="col-lg d-flex flex-wrap justify-content-end">
                <div class="row mx-auto mb-2  w-100 mt-2">
                  <div class="col-md-6">
                    <v-select
                      id="course"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="selectedCourse"
                      :reduce="pg => pg.id"
                      placeholder="Select Course"
                      :options="courses"
                      :loading="areCoursesLoading"
                      @input="onCourseFilterChange"
                    >
                    </v-select>
                  </div>
                </div>
              </div>
              <div v-if="isTabSmallScreen" class="col-lg d-flex flex-wrap justify-content-end">
                <div class="mb-2 mt-2" style="width: 100%;">
                  <div class="mb-2">
                    <v-select
                      id="course"
                      class="form-control v-select-custom"
                      label="title"
                      v-model="selectedCourse"
                      :reduce="pg => pg.id"
                      placeholder="Select Course"
                      :options="courses"
                      :loading="areCoursesLoading"
                      @input="onCourseFilterChange"
                    >
                    </v-select>
                  </div>
                </div>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="chapters"
              v-if="chapters.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              no-local-sorting
              @sort-changed="onSortChange"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(image_url)="data">
                <div class="text-center">
                  <img
                    v-if="data.item.image_url"
                    :src="data.item.image_url"
                    style="width: 50px; height: 50px; object-fit: cover; border-radius: 100%;"
                  />
                  <md-icon v-else class="text-muted" style="font-size: 50px;">
                    menu_book
                  </md-icon>
                </div>
              </template>
              <template #cell(order)="data">
                <div v-if="editingOrder.id === data.item.id" class="d-flex align-items-center">
                  <b-form-input
                    v-model.number="editingOrder.value"
                    type="number"
                    min="1"
                    class="mr-2"
                    style="width: 70px;"
                    @keyup.enter="saveOrder(data.item)"
                  />
                  <a href="#" @click="saveOrder(data.item)">
                    <md-icon class="text-success clickable-item mr-1">check</md-icon>
                  </a>
                  <a href="#" @click.stop="cancelEdit">
                    <md-icon class="text-danger clickable-item">close</md-icon>
                  </a>
                </div>
                <div v-else @click="startEditing(data.item)" class="clickable-item">
                  {{ data.item.order }}
                </div>
              </template>

              <template #cell(title)="data">
                <strong> {{ data.item.title }}</strong>
              </template>

              <template #cell(course)="data">
                <div>{{ (data.item.course && data.item.course.title) || 'N/A' }}</div>
              </template>

              <template #cell(estimated_time_minutes)="data">
                <div>{{ data.item.estimated_time_minutes ? `${data.item.estimated_time_minutes} min` : 'N/A' }}</div>
              </template>

              <template #cell(actions)="data">
                <router-link
                  :to="{
                    name: 'edit-chapter',
                    params: { id: data.item.id },
                  }"
                  class="svg-icon mr-2"
                  v-b-popover.hover.left
                  :title="$t('generalMsgs.edit')"
                >
                  <md-icon class="text-info">edit</md-icon>
                </router-link>

                <a
                  href="#"
                  @click.prevent="confirmDeleteChapter(data.item.id)"
                  v-b-popover.hover.left
                  :title="$t('generalMsgs.remove')"
                >
                  <md-icon class="text-danger">delete</md-icon>
                </a>
              </template>
            </b-table>

            <div class="card-footer" v-if="chapters.length">
              <pagination
                v-model="currentPage"
                :total-rows="totalChapters"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="chapters-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import Pagination from '../../../components/Ui/Pagination.vue';
import vSelect from 'vue-select';

export default {
  components: {
    Pagination,
    vSelect,
  },
  extends: Page,

  data() {
    return {
      title: 'View Chapters',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalChapters: 0,
      searchTerm: null,
      chapters: [],
      editingOrder: {
        id: null,
        value: null,
        originalValue: null,
      },
      courses: [],
      selectedCourse: null,
      areCoursesLoading: false,
      windowWidth: window.innerWidth,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isTabSmallScreen() {
      return this.windowWidth <= 767;
    },
    tableColumns() {
      return [
        { key: 'image_url', label: '', thStyle: { width: '70px' } },
        { key: 'order', label: 'Index', class: 'text-center' },
        { key: 'title', label: 'Title', class: 'text-center' },
        { key: 'course', label: 'Course', class: 'text-center' },
        { key: 'estimated_time_minutes', label: 'Duration', class: 'text-center' },
        { key: 'lesson_count', label: 'Lessons', class: 'text-center' },
        {
          key: 'actions',
          class: 'text-right',
          thStyle: { minWidth: '100px' },
        },
      ];
    },
  },

  methods: {
    ...mapActions('chapters', ['getChapters', 'deleteChapter', 'updateChapterOrder']),
    ...mapActions('courses', ['getLmsCourses']),

    async fetchChapters() {
      this.isLoading = true;
      try {
        const params = {
          school__id: this.getLoggedInUser.linked_entity.id,
          ...(this.currentPage && { page: this.currentPage }),
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.selectedCourse && { course__id: this.selectedCourse }),
        };

        const res = await this.getChapters(params);
        this.chapters = res.data || [];
        this.totalChapters = res.count || 0;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      } finally {
        this.isLoading = false;
      }
    },

    onPageChange(pageNum) {
      this.currentPage = pageNum;
      this.fetchChapters();
    },

    rowClicked(row) {
      this.$router.push({
        name: 'edit-chapter',
        params: { id: row.id },
      });
    },

    onSortChange() {
      // Sorting can be implemented here if needed
      // The context parameter is not used as sorting is not implemented yet
    },

    startEditing(chapter) {
      this.editingOrder = {
        id: chapter.id,
        value: chapter.order,
        originalValue: chapter.order,
      };
    },

    cancelEdit() {
      this.editingOrder = {
        id: null,
        value: null,
        originalValue: null,
      };
    },

    async saveOrder(chapter) {
      if (!this.editingOrder.value || this.editingOrder.value < 1) {
        this.makeToast({
          variant: 'danger',
          msg: 'Order must be a positive number',
        });
        return;
      }

      if (this.editingOrder.value === this.editingOrder.originalValue) {
        this.cancelEdit();
        return;
      }

      try {
        await this.updateChapterOrder({
          id: chapter.id,
          order: this.editingOrder.value,
        });

        this.makeToast({
          variant: 'success',
          msg: 'Chapter order updated successfully',
        });

        // Refresh the list to show updated order
        this.fetchChapters();
      } catch (error) {
        this.makeToast({
          variant: 'danger',
          msg: 'Failed to update chapter order. Please try again.',
        });
        console.error('Error updating chapter order:', error);
      } finally {
        this.cancelEdit();
      }
    },

    async fetchCourses() {
      this.areCoursesLoading = true;
      try {
        const response = await this.getLmsCourses({
          school__id: this.getLoggedInUser.linked_entity.id,
          page_size: 100, // Adjust based on your needs
          ordering: 'title',
        });
        this.courses = response.data || [];
      } catch (error) {
        console.error('Error fetching courses:', error);
        this.makeToast({
          variant: 'danger',
          msg: 'Failed to load courses. Please try again.',
        });
      } finally {
        this.areCoursesLoading = false;
      }
    },

    onCourseFilterChange() {
      this.currentPage = 1;
      this.fetchChapters();
    },

    onSearch: debounce(function() {
      this.currentPage = 1;
      this.fetchChapters();
    }, 500),

    async confirmDeleteChapter(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this chapter?', {
          title: 'Delete Chapter',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'success',
          cancelVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
        if (isConfirmed) {
          await this.deleteChapter(id);
          this.makeToast({ variant: 'success', msg: 'Chapter deleted successfully' });
          this.fetchChapters();
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
  },

  mounted() {
    this.fetchChapters();
    this.fetchCourses();
  },
};
</script>

<style scoped>
.clickable-item {
  cursor: pointer;
}
</style>
